import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { useAppContext } from "./AppContext";
import { useAuth } from "./AuthContext";
import websocketService from "../utils/websocket";
import { WS_EVENTS } from "../constants/index";
// Create WebSocket context
const WebSocketContext = createContext();

// Provider component
export const WebSocketProvider = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const { userData, podData } = useAppContext();
  const { serviceAccountToken } = useAuth();

  const currentSessionIdRef = useRef(userData?.sessionId);
  useEffect(() => {
    currentSessionIdRef.current = userData?.sessionId;
  }, [userData?.sessionId]);

  // subscribe() below is memoized with an empty dep array, so it must read
  // podData through a ref — otherwise wrappedHandler closes over whatever
  // podData was at mount (null, before loadPodData resolves) and the pod-IP
  // check would compare against a value that's stale for the component's life.
  const ourPodIpRef = useRef(podData?.ip);
  useEffect(() => {
    ourPodIpRef.current = podData?.ip;
  }, [podData?.ip]);

  // Connect to WebSocket when we have a token (welcome screen or authenticated)
  useEffect(() => {
    if (serviceAccountToken) {
      const patientId = userData?.patientId ?? null;
      console.log("Connecting to WebSocket, patientId:", patientId ?? "(welcome/unauthenticated)");
      websocketService.connect(patientId);
    }
  }, [serviceAccountToken, userData?.patientId]);

  // Set up event listeners
  useEffect(() => {
    const handleConnection = (data) => {
      console.log("WebSocket connection status:", data.status);
      setConnectionStatus(data.status);
      if (data.status === "connected") {
        setError(null);
      }
    };

    const handleMessage = (data) => {
      setLastMessage(data);
      console.log("WebSocket message received in context:", data);
    };

    const handleError = (error) => {
      setError(error.message);
      console.error("WebSocket error in context:", error);
    };

    const handleReconnect = (data) => {
      console.log("WebSocket reconnect attempt:", data.attempt);
      // You can add UI feedback here if needed
    };

    const handleCheckoutDone = (data) => {
      console.log("WebSocket checkout done:", data);
    };

    // Register event listeners
    websocketService.on("connection", handleConnection);
    websocketService.on("message", handleMessage);
    websocketService.on("error", handleError);
    websocketService.on("reconnect", handleReconnect);
    websocketService.on("checkout_done", handleCheckoutDone);

    // Cleanup on unmount
    return () => {
      websocketService.off("connection", handleConnection);
      websocketService.off("message", handleMessage);
      websocketService.off("error", handleError);
      websocketService.off("reconnect", handleReconnect);
    };
  }, []);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      websocketService.disconnect();
    };
  }, []);

  // Send message function
  const sendMessage = useCallback((type, payload) => {
    websocketService.send(type, payload);
  }, []);

  // Subscribe to specific events
  const subscribe = useCallback((eventType, handler) => {
    const wrappedHandler = (data) => {
      const expectedPodIp = ourPodIpRef.current;

      // No session exists yet to check against, so pod-IP is the only guard —
      // fail CLOSED (reject on missing data) unlike the generic check below.
      if (eventType === WS_EVENTS.SESSION_STARTED) {
        console.log(`[WebSocket] SESSION_STARTED received. Our pod IP: ${expectedPodIp}, Event pod IP: ${data?.podIp}`);
        if (!expectedPodIp || !data?.podIp || String(data.podIp) !== String(expectedPodIp)) {
          console.warn(`[WebSocket] Ignoring ${eventType} event for different/unknown pod. Expected: ${expectedPodIp}, Got: ${data?.podIp}`);
          return;
        }
        handler(data);
        return;
      }

      // Arrives after the session ended, so the live session id is already
      // cleared and the check below would drop it. Matched against the
      // persisted previous session id by the handler instead.
      if (eventType === WS_EVENTS.POD_SESSION_REPORT) {
        handler(data);
        return;
      }

      // Filter by Pod IP if available
      if (expectedPodIp && data?.podIp && String(data.podIp) !== String(expectedPodIp)) {
        console.warn(`[WebSocket] Ignoring ${eventType} event for different pod. Expected: ${expectedPodIp}, Got: ${data.podIp}`);
        return;
      }

      // Fail CLOSED: a session-scoped event with no matching local session gets rejected, not passed through.
      const currentSessionId = currentSessionIdRef.current;
      if (data?.sessionId && (!currentSessionId || String(data.sessionId) !== String(currentSessionId))) {
        console.warn(`[WebSocket] Ignoring ${eventType} event for different session. Expected: ${currentSessionId ?? "(none)"}, Got: ${data.sessionId}`);
        return;
      }

      handler(data);
    };

    websocketService.on(eventType, wrappedHandler);

    // Return unsubscribe function
    return () => {
      websocketService.off(eventType, wrappedHandler);
    };
  }, []);

  // Get connection status
  const getConnectionStatus = useCallback(() => {
    return websocketService.getStatus();
  }, []);

  // Check if connected
  const isConnected = useCallback(() => {
    return websocketService.isConnected();
  }, []);

  // Reconnect manually
  const reconnect = useCallback(() => {
    if (userData && userData.patientId) {
      websocketService.disconnect();
      websocketService.connect(userData.patientId);
    }
  }, [userData]);

  const value = {
    connectionStatus,
    lastMessage,
    error,
    sendMessage,
    subscribe,
    getConnectionStatus,
    isConnected,
    reconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook to use WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
